/**
 * Utilitaires pour la gestion des dates dans l'application Gestion Scolaire V3
 */

/**
 * Normalise une date au format YYYY-MM-DD en préservant le jour local
 * @param {Date|string} date - Date à normaliser (objet Date ou chaîne)
 * @returns {string} Date normalisée au format YYYY-MM-DD
 */
export const normalizeDateFormat = (date) => {
  if (typeof date === 'string') {
    // Si c'est déjà au format YYYY-MM-DD, on le retourne tel quel
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Si c'est au format ISO avec T, on extrait juste la partie date
    if (date.includes('T')) {
      return date.split('T')[0];
    }
    // Sinon on crée une date et on utilise la méthode ci-dessous
    date = new Date(date);
  }
  
  if (date instanceof Date) {
    // Utiliser les méthodes getFullYear, getMonth et getDate pour préserver le jour local
    const year = date.getFullYear();
    // getMonth() retourne 0-11, donc +1 pour avoir 1-12
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  return date;
};

/**
 * Génère un tableau de dates entre deux dates en préservant les jours locaux
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {Date[]} Tableau de dates
 */
export const generateDaysBetweenDates = (startDate, endDate) => {
  const days = [];
  
  // S'assurer que nous avons des objets Date
  const start = startDate instanceof Date ? new Date(startDate) : new Date(startDate);
  const end = endDate instanceof Date ? new Date(endDate) : new Date(endDate);
  
  // Réinitialiser les heures pour éviter les problèmes de comparaison
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  // Créer une copie pour itérer
  const currentDate = new Date(start);
  
  // Utiliser les dates locales pour la comparaison
  while (currentDate <= end) {
    // Créer une nouvelle instance pour chaque jour pour éviter les références
    days.push(new Date(currentDate));
    // Ajouter un jour en utilisant la méthode setDate qui respecte les jours locaux
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return days;
};

/**
 * Formate une date au format JJ/MM
 * @param {Date|string} date - Date à formater
 * @returns {string} Date formatée
 */
export const formatShortDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

/**
 * Obtient le nom court du jour de la semaine (Lun, Mar, etc.)
 * @param {Date|string} date - Date
 * @returns {string} Nom court du jour
 */
export const getShortDayName = (date) => {
  const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  return days[new Date(date).getDay()];
};

/**
 * Obtient le nom complet du jour de la semaine (Lundi, Mardi, etc.)
 * @param {Date|string} date - Date
 * @returns {string} Nom complet du jour
 */
export const getDayName = (date) => {
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  return days[new Date(date).getDay()];
};
